import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import { Heading } from './Typography';
import { MessageCard } from './MessageCard';
import { forgotPassword, emailWithPassword, logout, signUpWithInvite } from '../api';
import { EmailAndPassword } from './EmailOrPhoneAuth';
import { ErrorView } from './ErrorView';
import { useAppContainerContext } from './AppContainer';
import { useSetTokensWithRedirect } from '../hooks/useSetTokensWithRedirect';
import { useReturnTo } from '../hooks/useReturnTo';
import { useVerifyToken } from '../hooks/useVerifyToken';
import { PasswordField } from './Fields';
import { LoadingView } from './LoadingView';
import { matchHostname } from '@lendica/utils';

export const LoginWithInvitation = () => {
    const { loginValue, setError, setPageLoading } = useAppContainerContext();
    const navigate = useNavigate();

    const setTokensWithRedirect = useSetTokensWithRedirect();

    const handleForgotPassword = async () => {
        setPageLoading(true);
        setError(null);
        try {
            const data = await forgotPassword(loginValue.email || '');
            setTimeout(() => {
                navigate('../../reset');
                setPageLoading(false);
            }, 0);
        } catch (error) {
            setError('Failed to send reset email. Please try again.');
            setPageLoading(false);
        }
    };

    const handleSubmit = async (
        { email, password }: { email: string; password: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        try {
            setSubmitting(true);
            const tokens = await emailWithPassword(email, password);
            setTokensWithRedirect(tokens);
        } catch (error) {
            setError('Invalid email or password');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <EmailAndPassword
            email={loginValue.email || ''}
            handleForgotPassword={handleForgotPassword}
            handleSubmit={handleSubmit}
            includeSignUpLink={false}
        />
    );
};

export const LogoutAndNext = () => {
    const navigate = useNavigate();
    const returnTo = useReturnTo();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { loginValue, setError } = useAppContainerContext();

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await logout();
            if (returnTo) {
                navigate(returnTo);
            }
        } catch (error) {
            setError('Failed to sign out. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Sign out to continue</Heading>
            <MessageCard>
                {`You've been invited to join a team as `}
                <em
                    style={{
                        display: 'inline-block',
                        verticalAlign: '-0.15em',
                        fontSize: '21px',
                        fontWeight: 'inherit',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '296px',
                        lineHeight: '1.12',
                    }}
                    title={loginValue.email || ''}
                >
                    {loginValue.email || ''}
                </em>
                {`, but you're signed in with a different account. Please sign out to continue.`}
            </MessageCard>
            <LoadingButton
                type="button"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={handleSubmit}
            >
                Sign out and next
            </LoadingButton>
        </Box>
    );
};

export const SwitchAccount = ({
    heading = 'Switch account to join team',
    message = "You've been invited to join a team, but you're signed in with a different email.",
}) => {
    const { loginValue, setError } = useAppContainerContext();
    const setTokensWithRedirect = useSetTokensWithRedirect();

    const handleSubmit = async (
        { email, password }: { email: string; password: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        try {
            setSubmitting(true);
            setError(null);
            const tokens = await emailWithPassword(email, password);
            setTokensWithRedirect(tokens);
        } catch (error) {
            setError('Invalid email or password');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>{heading}</Heading>
            <MessageCard>{message}</MessageCard>
            <Formik
                initialValues={{
                    email: loginValue.email || '',
                    password: '',
                }}
                onSubmit={handleSubmit}
                validationSchema={Yup.object({
                    email: Yup.string().email('Enter a valid email'),
                    password: Yup.string().min(8, 'At least 8 characters').required('Required'),
                })}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                        <Stack sx={{ width: '100%', mb: 2.5 }}>
                            <TextField
                                name="email"
                                label="Email"
                                fullWidth
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={(touched.email && errors.email) || ' '}
                                disabled={!!loginValue.email}
                            />
                            <PasswordField
                                name="password"
                                label="Password"
                                fullWidth
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && !!errors.password}
                                helperText={(touched.password && errors.password) || ' '}
                            />
                        </Stack>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            Log into invited account
                        </LoadingButton>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

export const SignupWithInvitation = () => {
    const navigate = useNavigate();
    const setTokensWithRedirect = useSetTokensWithRedirect(() => {
        setSuccessMsg('Your account is ready. Redirecting to home...');
        setTimeout(() => {
            setPageLoading(false);
            navigate('/');
        }, 1500);
    });
    const { loginValue, setPageLoading, setSuccessMsg, setError } = useAppContainerContext();
    const verifyToken = useVerifyToken();

    const [shouldErrorRedirect, setShouldErrorRedirect] = useState(false);

    useEffect(() => {
        if (verifyToken) return;
        const timeoutId = setTimeout(() => setShouldErrorRedirect(true), 300);
        return () => clearTimeout(timeoutId);
    }, [verifyToken]);

    useEffect(() => {
        if (!verifyToken && shouldErrorRedirect) {
            navigate('../error', { replace: true });
        }
    }, [verifyToken, shouldErrorRedirect, navigate]);

    if (!verifyToken) {
        return <LoadingView />;
    }

    const handleSubmit = async (
        {
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
        }: {
            firstName: string;
            lastName: string;
            email: string;
            password: string;
            confirmPassword: string;
        },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        setSubmitting(true);
        setPageLoading(true);
        setSuccessMsg(null);
        setError(null);
        try {
            const res = await signUpWithInvite(verifyToken, {
                first_name: firstName,
                last_name: lastName,
                email,
                password,
                confirm_password: confirmPassword,
            });
            if ('access' in res && 'refresh' in res) {
                setTokensWithRedirect({ access: res.access, refresh: res.refresh });
                // setSuccessMsg(res.message || 'Your account is ready. Redirecting to home...');
            } else {
                setPageLoading(false);
                setError(res.message || 'Something went wrong.');
                setTimeout(() => {
                    setError(null);
                }, 2500);
            }
        } catch (error) {
            setPageLoading(false);
            setError('Failed to sign up. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Set up your account</Heading>
            <MessageCard>Sign up to join your team.</MessageCard>
            <Formik
                initialValues={{
                    firstName: '',
                    lastName: '',
                    email: loginValue.email || '',
                    password: '',
                    confirmPassword: '',
                }}
                onSubmit={handleSubmit}
                validationSchema={Yup.object({
                    firstName: Yup.string().required('First name is required'),
                    lastName: Yup.string().required('Last name is required'),
                    email: Yup.string().email('Enter a valid email'),
                    password: Yup.string().min(8, 'At least 8 characters').required('Required'),
                    confirmPassword: Yup.string()
                        .min(8, 'At least 8 characters')
                        .oneOf([Yup.ref('password')], 'Passwords must match')
                        .required('Required'),
                })}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
                    >
                        <Stack sx={{ width: '100%', mb: 2.5 }}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    name="firstName"
                                    label="First Name"
                                    fullWidth
                                    value={values.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.firstName && !!errors.firstName}
                                    helperText={(touched.firstName && errors.firstName) || ' '}
                                />
                                <TextField
                                    name="lastName"
                                    label="Last Name"
                                    fullWidth
                                    value={values.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.lastName && !!errors.lastName}
                                    helperText={(touched.lastName && errors.lastName) || ' '}
                                />
                            </Stack>
                            <TextField
                                name="email"
                                label="Email"
                                fullWidth
                                value={values.email}
                                disabled={!!loginValue.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={(touched.email && errors.email) || ' '}
                            />
                            <PasswordField
                                name="password"
                                label="Password"
                                fullWidth
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && !!errors.password}
                                helperText={(touched.password && errors.password) || ' '}
                            />
                            <PasswordField
                                name="confirmPassword"
                                label="Confirm Password"
                                fullWidth
                                value={values.confirmPassword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.confirmPassword && !!errors.confirmPassword}
                                helperText={
                                    (touched.confirmPassword && errors.confirmPassword) || ' '
                                }
                            />
                        </Stack>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            Sign up
                        </LoadingButton>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

export const InvitationErrorFallback = () => {
    return (
        <ErrorView message="You may have been invited to join a team, but the link is invalid or expired. Please contact the team owner to get a new link." />
    );
};

export const LogoutAndApply = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { loginValue, setError } = useAppContainerContext();

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await logout();
            window.location.href = `https://apply.${matchHostname()}?token=${
                loginValue.verifyToken
            }&email=${encodeURIComponent(
                loginValue.email || ''
            )}&utm_source=product-invite&utm_medium=portal`;
        } catch (error) {
            setError('Failed to sign out. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Sign out to continue</Heading>
            <MessageCard>
                {`You've been invited as `}
                <em
                    style={{
                        display: 'inline-block',
                        verticalAlign: '-0.15em',
                        fontSize: '21px',
                        fontWeight: 'inherit',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        maxWidth: '296px',
                        lineHeight: '1.12',
                    }}
                    title={loginValue.email || ''}
                >
                    {loginValue.email || ''}
                </em>
                {`, but you're signed in with a different account. Please sign out to continue.`}
            </MessageCard>
            <LoadingButton
                type="button"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={handleSubmit}
            >
                Sign out and next
            </LoadingButton>
        </Box>
    );
};
